import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchProducts, verifyProduct } from '../utils/api';
import ProductDetailsModal from '../components/ProductDetailsModal';
import HeroSlider from '../components/HeroSlider';
import { useLanguage } from '../contexts/LanguageContext';
import { Search, Shield, Award, Users, Microscope, Heart, ArrowRight, ArrowLeft } from 'lucide-react';
import ProductCard from '../components/ProductCard';

const Home = () => {
  const { t, isRTL, isArabic } = useLanguage();
  const [scratchCode, setScratchCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationMessage, setVerificationMessage] = useState({ text: '', type: '' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const maxFeaturedProducts = 8; // Limit to 8 products on home page

  // Sample products for fallback when no products are loaded
  const sampleProducts = [
    {
      id: 'sample1',
      name: 'Medical Surgical Mask',
      category: 'Medical Supplies',
      image: 'placeholder.jpg',
      description: 'High-quality surgical mask for medical professionals',
      details: 'Disposable, 3-layer protection, FDA approved',
      price: '25.00'
    },
    {
      id: 'sample2', 
      name: 'Digital Thermometer',
      category: 'Medical Devices',
      image: 'placeholder.jpg',
      description: 'Accurate digital thermometer for temperature measurement',
      details: 'Fast reading, memory function, fever alarm',
      price: '35.00'
    },
    {
      id: 'sample3',
      name: 'Latex Examination Gloves',
      category: 'Medical Supplies', 
      image: 'placeholder.jpg',
      description: 'Sterile latex gloves for medical examination',
      details: 'Powder-free, disposable, various sizes available',
      price: '15.00'
    },
    {
      id: 'sample4',
      name: 'Stethoscope Professional',
      category: 'Medical Devices',
      image: 'placeholder.jpg', 
      description: 'Professional stethoscope for medical diagnosis',
      details: 'Dual head, excellent acoustics, lightweight',
      price: '120.00'
    },
    {
      id: 'sample5',
      name: 'Blood Pressure Monitor',
      category: 'Medical Devices',
      image: 'placeholder.jpg',
      description: 'Digital blood pressure monitor for home use',
      details: 'Large display, memory storage, accurate readings',
      price: '85.00'
    },
    {
      id: 'sample6',
      name: 'First Aid Kit Complete',
      category: 'Medical Supplies',
      image: 'placeholder.jpg',
      description: 'Complete first aid kit for emergency situations',
      details: 'Comprehensive supplies, portable case, emergency guide',
      price: '45.00'
    },
    {
      id: 'sample7',
      name: 'Pulse Oximeter',
      category: 'Medical Devices',
      image: 'placeholder.jpg',
      description: 'Fingertip pulse oximeter for oxygen saturation monitoring',
      details: 'LED display, automatic shut-off, portable design',
      price: '65.00'
    },
    {
      id: 'sample8',
      name: 'Wheelchair Standard',
      category: 'Medical Equipment',
      image: 'placeholder.jpg',
      description: 'Standard manual wheelchair for mobility assistance',
      details: 'Foldable, adjustable footrests, comfortable seating',
      price: '350.00'
    }
  ];

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchProducts();
        if (data && data.length > 0) {
          setAllProducts(data);
          // Set featured products (limited to first few)
          setFeaturedProducts(data.slice(0, maxFeaturedProducts));
        } else {
          // Use sample products if no real products are available
          setAllProducts(sampleProducts);
          setFeaturedProducts(sampleProducts.slice(0, maxFeaturedProducts));
        }
      } catch (error) {
        console.error('Error loading products:', error);
        setError('Failed to load products. Showing sample products.');
        // Use sample products as fallback
        setAllProducts(sampleProducts);
        setFeaturedProducts(sampleProducts.slice(0, maxFeaturedProducts));
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const displayProducts = featuredProducts; // Show all featured products without filtering

  const handleVerification = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setVerificationMessage({ text: t('enterVerificationCode') || 'Please enter a verification code', type: 'error' });
      return;
    }

    try {
      setIsVerifying(true);
      const response = await verifyProduct(verificationCode.trim());
      setVerificationMessage({ text: response.message, type: response.status });
      setVerificationCode('');
    } catch {
      setVerificationMessage({
        text: t('verificationError'),
        type: 'error',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 ${isRTL ? 'font-arabic' : 'font-english'}`} dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <section className="pt-2 sm:pt-4 pb-8 sm:pb-12 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <HeroSlider />
        </div>
      </section>

      {/* Product Verification Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('verification') || 'Product Verification'}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-6 sm:mb-8 px-2">
              {t('verificationDesc') || 'Easy verification system for authenticity'}
            </p>

            <div className="flex justify-center px-2">
              <form onSubmit={handleVerification} className={`flex flex-col gap-3 w-full max-w-md sm:max-w-lg md:max-w-2xl ${isRTL ? 'sm:flex-row-reverse' : 'sm:flex-row'} sm:gap-3`}>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder={t('verifyPlaceholder')}
                  className={`flex-1 px-3 sm:px-4 md:px-6 py-3 md:py-4 text-sm sm:text-base md:text-lg bg-gray-50 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                />
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-700 active:scale-95 transition-all text-white text-sm sm:text-base md:text-lg font-semibold px-4 sm:px-6 md:px-8 py-3 md:py-4 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0"
                  disabled={isVerifying}
                >
                  {isVerifying ? (t('loading') || 'Verifying...') : t('verifyBtn')}
                </button>
              </form>
            </div>
          </motion.div>

          {verificationMessage.text && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`p-6 text-lg rounded-xl max-w-2xl mx-auto border-2 ${
                verificationMessage.type === 'success'
                  ? 'bg-green-50 text-green-800 border-green-200'
                  : 'bg-red-50 text-red-800 border-red-200'
              }`}
            >
              {verificationMessage.text}
            </motion.div>
          )}

          {error && (
            <div className="mt-6 p-6 text-lg bg-red-50 text-red-800 border-2 border-red-200 rounded-xl max-w-2xl mx-auto">
              {error}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('featuredProducts')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              {t('exploreProducts')}
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center">
                <svg className="animate-spin h-8 w-8 text-primary-600 mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-lg text-gray-600">{t('loading')}</span>
              </div>
            </div>
          ) : displayProducts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl text-gray-300 mb-4">📦</div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                {t('noProductsFound')}
              </h3>
              <p className="text-gray-500 mb-6">
                {t('noProducts')}
              </p>
              <Link
                to="/products"
                className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300"
              >
                {t('exploreProducts') || 'Explore All Products'}
              </Link>
            </div>
          ) : (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12"
              >
                {displayProducts.map((product, index) => (
                  <motion.div key={product.id} variants={itemVariants}>
                    <ProductCard
                      product={product}
                      onClick={() => handleProductClick(product)}
                    />
                  </motion.div>
                ))}
              </motion.div>

              {/* View All Products Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-center"
              >
                <Link
                  to="/products"
                  className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl group"
                >
                  {t('viewAllProducts')}
                  {isArabic ? (
                    <ArrowLeft className="ml-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  ) : (
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </Link>
                <p className="text-gray-600 mt-4">
                  {t('discoverMore') || `Discover all ${allProducts.length} medical products`}
                </p>
              </motion.div>
            </>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('whyChooseUs')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('whyChooseUsDesc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: t('qualityAssurance'), desc: t('qualityAssuranceDesc') },
              { icon: Award, title: t('certified'), desc: t('certifiedDesc') },
              { icon: Users, title: t('trustedBy'), desc: t('trustedByDesc') },
              { icon: Microscope, title: t('innovation'), desc: t('innovationDesc') },
              { icon: Heart, title: t('patientCare'), desc: t('patientCareDesc') },
              { icon: Search, title: t('verification'), desc: t('verificationDesc') }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <feature.icon className="w-12 h-12 text-primary-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Details Modal */}
      <ProductDetailsModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default Home;
